import { useTheme } from '@emotion/react';
import cn from 'classnames';

import { EColorMap } from '../constants/colorMap';
import { IConDefaultType } from '../types';

/**
 * `*_NEW` variant의 미확인 알림 배지(점)는 `brand.primary`를 쓴다.
 * 시안 기준: Tingle DS Nav Bar(1762:116245)가 참조하는 변수가 brand/primary(팅글 #F95388)다.
 * 이전엔 `static.warning`(= system.red)이라 팅글에서 노랑이어야 할 점이 빨강으로 나갔다.
 * 배지는 경고가 아니라 브랜드 강조 요소이므로 경고 토큰을 쓰지 않는다.
 */
export enum AlarmIconVariant {
  DEFAULT = 'default',
  NEW = 'new',
  SMALL = 'small',
  FILL = 'fill',
  FILL_NEW = 'fillNew',
  /** Tingle DS 알림 아이콘(외곽선) — FILL과 같은 v2 종 모양의 라인 버전 */
  OUTLINE = 'outline',
  OUTLINE_NEW = 'outlineNew',
}

interface IAlarmIconProps extends IConDefaultType {
  variant?: AlarmIconVariant;
  backgroundColor?: string;
}

function AlarmIcon({
  color,
  className,
  size = 24,
  rotate,
  flip,
  variant = AlarmIconVariant.DEFAULT,
  backgroundColor = 'white',
}: IAlarmIconProps) {
  const fill = color || EColorMap.gray_0;
  const theme = useTheme();
  return (
    <i
      className={cn('dds-icon', className, { flip })}
      style={{
        width: size,
        minWidth: size,
        height: size,
        transform: `rotate(${rotate}deg)`,
      }}
    >
      {variant === AlarmIconVariant.DEFAULT && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_17_2602)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.43387 19.1757C9.88912 18.863 10.5116 18.9786 10.8243 19.4338C10.9389 19.6006 11.0956 19.7323 11.2772 19.8157C11.2904 19.8218 11.3035 19.8281 11.3164 19.8347C11.6313 19.9963 11.9921 20.041 12.3359 19.9617C12.6798 19.8823 12.9864 19.6835 13.2013 19.3983C13.5336 18.9572 14.1606 18.8689 14.6017 19.2012C15.0428 19.5335 15.131 20.1605 14.7987 20.6017C14.302 21.261 13.5897 21.7249 12.7856 21.9105C11.9884 22.0944 11.1527 21.9928 10.4228 21.6241C9.92027 21.389 9.4899 21.0236 9.17569 20.5661C8.86302 20.1108 8.97861 19.4883 9.43387 19.1757Z"
              fill={fill}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 4C10.6739 4 9.40215 4.52678 8.46447 5.46447C7.52678 6.40215 7 7.67392 7 9V9.76C6.99907 10.8205 6.57703 11.8381 5.82667 12.5875L5.46711 12.9471C5.16893 13.2448 5.00059 13.6493 5 14.0706V14.41C5 14.8317 5.16752 15.2361 5.4657 15.5343C5.76388 15.8325 6.16831 16 6.59 16H17.41C17.8317 16 18.2361 15.8325 18.5343 15.5343C18.8289 15.2397 18.9959 14.8415 18.9999 14.4253C18.9863 14.0868 18.8642 13.7615 18.6514 13.4975L18.6377 13.4801L17.8733 12.4874C17.3101 11.7815 17.0022 10.9057 17 10.0024L17 10V9C17 7.67392 16.4732 6.40215 15.5355 5.46447C14.5979 4.52678 13.3261 4 12 4ZM7.05025 4.05025C8.36301 2.7375 10.1435 2 12 2C13.8565 2 15.637 2.7375 16.9497 4.05025C18.2625 5.36301 19 7.14349 19 9V9.9986C19.0013 10.4516 19.1565 10.8908 19.44 11.2442L19.4523 11.2599L20.2161 12.2519C20.7008 12.8574 20.9758 13.604 20.9995 14.3794C20.9998 14.3896 21 14.3998 21 14.41C21 15.3621 20.6218 16.2753 19.9485 16.9485C19.2753 17.6218 18.3621 18 17.41 18H6.59C5.63787 18 4.72474 17.6218 4.05149 16.9485C3.37823 16.2753 3 15.3621 3 14.41L3 14.0689C3.00103 13.1172 3.37993 12.2049 4.05339 11.5324C4.05337 11.5324 4.0534 11.5324 4.05339 11.5324L4.41289 11.1729C4.78799 10.7983 4.99944 10.2896 5 9.75949V9C5 7.14348 5.7375 5.36301 7.05025 4.05025Z"
              fill={fill}
            />
          </g>
          <defs>
            <clipPath id="clip0_17_2602">
              <rect width="24" height="24" fill={backgroundColor} />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === AlarmIconVariant.SMALL && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" fill={backgroundColor} />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.2778 17.7414C10.6512 17.4914 11.1618 17.5838 11.4183 17.9479C11.5122 18.0813 11.6408 18.1866 11.7898 18.2533C11.8006 18.2581 11.8113 18.2632 11.8219 18.2685C12.0802 18.3977 12.3761 18.4334 12.6581 18.37C12.9402 18.3065 13.1917 18.1475 13.3679 17.9194C13.6404 17.5667 14.1547 17.4961 14.5165 17.7619C14.8783 18.0276 14.9507 18.529 14.6782 18.8818C14.2707 19.4091 13.6865 19.7801 13.0269 19.9285C12.373 20.0756 11.6876 19.9943 11.0889 19.6994C10.6768 19.5115 10.3238 19.2192 10.0661 18.8534C9.8096 18.4893 9.90441 17.9914 10.2778 17.7414Z"
            fill={fill}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.3819 5.59942C11.2942 5.59942 10.2511 6.02069 9.48203 6.77056C8.71293 7.52043 8.28085 8.53748 8.28085 9.59795V10.2057C8.28009 11.0538 7.93392 11.8676 7.31847 12.4669L7.02355 12.7545C6.77899 12.9926 6.64091 13.316 6.64043 13.653V13.9244C6.64043 14.2616 6.77783 14.585 7.0224 14.8235C7.26697 15.0619 7.59869 15.1959 7.94457 15.1959H16.8193C17.1652 15.1959 17.4969 15.0619 17.7414 14.8235C17.983 14.5879 18.1201 14.2694 18.1234 13.9366C18.1122 13.6659 18.012 13.4058 17.8375 13.1947L17.8262 13.1807L17.1993 12.3869C16.7373 11.8224 16.4848 11.122 16.483 10.3996L16.483 10.3977V9.59795C16.483 8.53748 16.0509 7.52043 15.2818 6.77056C14.5127 6.02069 13.4696 5.59942 12.3819 5.59942ZM8.32207 5.6396C9.39881 4.58978 10.8592 4 12.3819 4C13.9047 4 15.365 4.58978 16.4418 5.6396C17.5185 6.68942 18.1234 8.11329 18.1234 9.59795V10.3965C18.1245 10.7589 18.2518 11.1101 18.4843 11.3927L18.4944 11.4052L19.1209 12.1985C19.5184 12.6827 19.744 13.2798 19.7635 13.8999C19.7637 13.908 19.7638 13.9162 19.7638 13.9244C19.7638 14.6858 19.4536 15.416 18.9014 15.9544C18.3492 16.4929 17.6002 16.7953 16.8193 16.7953H7.94457C7.16362 16.7953 6.41466 16.4929 5.86244 15.9544C5.31023 15.416 5 14.6858 5 13.9244L5 13.6516C5.00085 12.8905 5.31162 12.1609 5.864 11.6231C5.86399 11.6231 5.86402 11.6231 5.864 11.6231L6.15887 11.3356C6.46653 11.036 6.63996 10.6293 6.64043 10.2053V9.59795C6.64043 8.11329 7.24533 6.68942 8.32207 5.6396Z"
            fill={fill}
          />
        </svg>
      )}
      {variant === AlarmIconVariant.NEW && (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.80778 15.9806C8.19675 15.7201 8.72862 15.8164 8.99576 16.1957C9.09363 16.3346 9.22751 16.4443 9.38273 16.5138C9.39399 16.5188 9.40514 16.5241 9.41617 16.5296C9.68526 16.6642 9.99348 16.7015 10.2872 16.6354C10.5811 16.5693 10.843 16.4036 11.0266 16.1661C11.3105 15.7986 11.8462 15.7251 12.2231 16.0019C12.6 16.2787 12.6754 16.801 12.3915 17.1685C11.9671 17.7178 11.3585 18.1042 10.6715 18.2588C9.9903 18.412 9.27633 18.3274 8.65268 18.0202C8.22336 17.8244 7.85565 17.52 7.5872 17.1389C7.32006 16.7596 7.41882 16.2411 7.80778 15.9806Z"
            fill={fill}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 3.33274C8.86704 3.33274 7.78046 3.77157 6.97932 4.55268C6.17817 5.3338 5.7281 6.39322 5.7281 7.49788V8.13098C5.7273 9.01441 5.36671 9.86211 4.72561 10.4864L4.41841 10.7859C4.16365 11.0339 4.01982 11.3708 4.01932 11.7218V12.0046C4.01932 12.3558 4.16245 12.6927 4.41721 12.9411C4.67197 13.1895 5.01751 13.3291 5.3778 13.3291H14.6223C14.9826 13.3291 15.3281 13.1895 15.5829 12.9411C15.8345 12.6957 15.9773 12.364 15.9807 12.0173C15.969 11.7353 15.8647 11.4643 15.6829 11.2444L15.6712 11.2299L15.0181 10.403C14.5369 9.81497 14.2739 9.08539 14.272 8.33295L14.272 8.33091V7.49788C14.272 6.39322 13.8219 5.3338 13.0207 4.55268C12.2196 3.77157 11.133 3.33274 10 3.33274ZM5.77103 3.3746C6.89263 2.28104 8.41385 1.66669 10 1.66669C11.5862 1.66669 13.1074 2.28104 14.229 3.3746C15.3506 4.46816 15.9807 5.95135 15.9807 7.49788V8.32974C15.9819 8.70714 16.1144 9.07299 16.3567 9.36737L16.3672 9.38044L17.0198 10.2068C17.4339 10.7112 17.6688 11.3331 17.6891 11.979C17.6894 11.9875 17.6895 11.9961 17.6895 12.0046C17.6895 12.7977 17.3664 13.5584 16.7911 14.1192C16.2159 14.6801 15.4357 14.9951 14.6223 14.9951H5.3778C4.56431 14.9951 3.78414 14.6801 3.20892 14.1192C2.6337 13.5584 2.31055 12.7977 2.31055 12.0046L2.31055 11.7204C2.31143 10.9276 2.63515 10.1676 3.21055 9.60744C3.21053 9.60745 3.21056 9.60742 3.21055 9.60744L3.5177 9.30796C3.83818 8.99588 4.01884 8.57216 4.01932 8.13055V7.49788C4.01932 5.95135 4.64943 4.46816 5.77103 3.3746Z"
            fill={fill}
          />
          <ellipse
            cx="14.773"
            cy="4.58332"
            rx="2.91667"
            ry="2.91667"
            transform="rotate(90 14.773 4.58332)"
            fill={theme.color.brand.primary}
          />
        </svg>
      )}
      {(variant === AlarmIconVariant.FILL || variant === AlarmIconVariant.FILL_NEW) && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(2.9 2) scale(0.909)">
            <path
              d="M12.0944 19.6414C12.5961 19.441 13.1657 19.6855 13.3664 20.1871C13.5668 20.6888 13.3224 21.2584 12.8207 21.4591C11.0172 22.1803 9.00503 22.1803 7.20148 21.4591C6.69995 21.2583 6.45541 20.6887 6.6558 20.1871C6.85644 19.6855 7.42609 19.4411 7.92777 19.6414C9.26494 20.1762 10.7573 20.1762 12.0944 19.6414Z"
              fill={fill}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.0111 0C12.001 2.4513e-06 13.909 0.784363 15.3159 2.17888C16.7225 3.5733 17.5138 5.4643 17.5139 7.43685L17.5263 8.28833C17.6483 12.441 18.6532 15.0613 19.5905 16.6197C19.7022 16.8053 19.8019 16.9748 19.8839 17.1128C20.0636 17.4151 20.0673 17.7904 19.8934 18.0961C19.7194 18.4019 19.3947 18.5912 19.0429 18.5912H0.9783C0.626576 18.5911 0.301714 18.4019 0.127774 18.0961C-0.0459821 17.7905 -0.0422613 17.4151 0.13733 17.1128C0.222196 16.97 0.320058 16.8052 0.43167 16.6197C1.36905 15.0613 2.37384 12.4411 2.49587 8.28833L2.50829 7.43685C2.50841 5.46436 3.2998 3.57329 4.70628 2.17888C6.11307 0.784322 8.02122 6.847e-05 10.0111 0Z"
              fill={fill}
            />
          </g>
          {variant === AlarmIconVariant.FILL_NEW && (
            <circle cx="17.6" cy="5.6" r="3.3" fill={theme.color.brand.primary} />
          )}
        </svg>
      )}
      {(variant === AlarmIconVariant.OUTLINE || variant === AlarmIconVariant.OUTLINE_NEW) && (
        /* Tingle DS Nav Bar 아이콘(536:99205) — 28 프레임 안 글리프 19.11×21(높이 75%) 비율을
           24 viewBox로 환산. size={28}로 쓰면 시안과 동일한 크기가 된다. */
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(3.8095 3) scale(0.818182)">
            <path
              d="M12.0944 19.6414C12.5961 19.441 13.1657 19.6855 13.3664 20.1871C13.5668 20.6888 13.3224 21.2584 12.8207 21.4591C11.0172 22.1803 9.00503 22.1803 7.20148 21.4591C6.69995 21.2583 6.45541 20.6887 6.6558 20.1871C6.85644 19.6855 7.42609 19.4411 7.92777 19.6414C9.26494 20.1762 10.7573 20.1762 12.0944 19.6414Z"
              fill={fill}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.0111 0C12.001 2.4513e-06 13.909 0.784363 15.3159 2.17888C16.7225 3.5733 17.5138 5.4643 17.5139 7.43685L17.5263 8.28833C17.6483 12.441 18.6532 15.0613 19.5905 16.6197C19.7022 16.8053 19.8019 16.9748 19.8839 17.1128C20.0636 17.4151 20.0673 17.7904 19.8934 18.0961C19.7194 18.4019 19.3947 18.5912 19.0429 18.5912H0.9783C0.626576 18.5911 0.301714 18.4019 0.127774 18.0961C-0.0459821 17.7905 -0.0422613 17.4151 0.13733 17.1128C0.222196 16.97 0.320058 16.8052 0.43167 16.6197C1.36905 15.0613 2.37384 12.4411 2.49587 8.28833L2.50829 7.43685C2.50841 5.46436 3.2998 3.57329 4.70628 2.17888C6.11307 0.784322 8.02122 6.847e-05 10.0111 0ZM10.0111 1.95717C8.53526 1.95724 7.12305 2.53878 6.08433 3.56839C5.04621 4.59756 4.46556 5.98948 4.46546 7.43685C4.46546 11.6505 3.63215 14.6113 2.64495 16.634H17.3772C16.3901 14.6113 15.5567 11.6504 15.5567 7.43685C15.5566 5.98942 14.976 4.59757 13.9378 3.56839C12.8991 2.53881 11.4869 1.95717 10.0111 1.95717Z"
              fill={fill}
            />
          </g>
          {variant === AlarmIconVariant.OUTLINE_NEW && (
            <circle cx="17.6" cy="5.6" r="3.3" fill={theme.color.brand.primary} />
          )}
        </svg>
      )}
    </i>
  );
}

export default AlarmIcon;
